/**
 * Ember equipment as data. Everything a body wears or holds -- shirts, leg wear, footwear, armour,
 * a bauldron, a belt, shields and weapons -- is a MODEL: a record that says, in the expression
 * language (expr.ts), which volumes to hang on the solved skeleton, how to cut them and how to
 * paint them. A DESIGN binds an item type to a model and the values of its parameters (a
 * `longsleeve_laced` is the `shirt` model with long sleeves and a laced neck). The engine (body.ts)
 * supplies the body's own volumes, the primitive shapes, the light and the materials; models compose
 * them. The rig and its clips are the engine's; a model follows them in every pose and facing.
 *
 * The game's models and designs live in content (`content/game/sprites/ember/equipment.json`);
 * Sprite Lab makes more. Records only: what a model may call is what the engine hands it
 * (`setModelBuiltins`). Under them is the BODY LAYER (`bodies.json`, `hair.json`): the body itself
 * -- flesh, bones, a beast's head, wounds -- and hair are models too, of the `body` and `hair`
 * slots, and their library (the body's own volumes, which garments are inflated copies of) is in
 * scope for every equipment model.
 *
 * Order matters and the content compiler sorts object keys, so everything ordered is a list:
 * `let` is a list of [name, value] pairs, `parts` a list of part specs.
 */
import { Compiled, ExprError, Scope, compileFnSource, expr } from "./expr"

export type Json = null | boolean | number | string | Json[] | { [key: string]: Json }

/**
 * A named value: a string is an expression; `{ "fn": [...params] }` a function (FnSpec); any
 * other JSON is literal data (a table of colours, a list of numbers).
 */
export type LetValue = Json
export type LetList = [string, LetValue][]

/**
 * A function with steps, for paint and cuts that read better as early returns than as one
 * expression: its parameters, values named in order, then `cases` -- [condition, value] pairs, the
 * first whose condition holds is the result -- and otherwise `return` (undefined when absent).
 */
export interface FnSpec {
    fn: string[]
    let?: LetList
    cases?: [string, LetValue][]
    return?: LetValue
}

/**
 * What a model adds, in order:
 * - an expression: a primitive, a list of them, or nothing (false / undefined);
 * - `{ when, parts, else }`: those parts only when the condition holds;
 * - `{ each, as, let, parts }`: the parts once per item of a list (`each: "SIDES", as: "side"`);
 * - `{ let, parts }`: the parts with more names in scope;
 * - `{ paint, over, parts }`: the parts painted by a (world point, normal) => colour function
 *   (`over`: over their own paint, which still shows where it gives nothing); no paint (undefined)
 *   leaves them as they are;
 * - `{ apply, parts }`: the parts passed through a function (prims => prims): a texture over every
 *   volume, a beast's fur; none (undefined) leaves them as they are;
 * - `{ use, with, bind }`: another model of the same slot (or `slot/model`), with these parameters
 *   (`with` literal values, `bind` expressions) over the current ones.
 */
export type PartSpec =
    | string
    | { when: string; parts: PartSpec[]; else?: PartSpec[] }
    | { each: string; as: string; let?: LetList; parts: PartSpec[] }
    | { let: LetList; parts: PartSpec[] }
    | { paint: LetValue; over?: boolean; parts: PartSpec[] }
    | { apply: string; parts: PartSpec[] }
    | { use: string; with?: Record<string, Json>; bind?: Record<string, string> }

export interface EmberModel {
    about?: string
    /** Its parameters and their defaults; a design or a look supplies the rest. */
    params?: Record<string, Json>
    let?: LetList
    parts: PartSpec[]
    /**
     * Paint laid over the BODY's own surfaces of these groups once everything is built (a strap
     * across whatever the chest wears): a (world point, normal) => colour function.
     */
    overBody?: { groups: string[]; paint: LetValue }
}

export type EmberSlot = "shirt" | "pants" | "shoes" | "belt" | "armor" | "bauldron" | "shield" | "held"
export const EMBER_SLOTS: readonly EmberSlot[] = ["pants", "shoes", "shirt", "belt", "armor", "bauldron", "shield", "held"]
/** The body layer's slots: the body itself (bodies.json) and its hair (hair.json). */
export type EmberBodySlot = "body" | "hair"
export const EMBER_BODY_SLOTS: readonly EmberBodySlot[] = ["body", "hair"]
export type EmberModelSlot = EmberSlot | EmberBodySlot
/** The game's item class for each slot's items (a belt is only ever part of a look). */
export const EMBER_SLOT_CLASS: Record<EmberSlot, string | undefined> = {
    shirt: "shirt", pants: "pants", shoes: "shoes", belt: undefined, armor: "armor", bauldron: "bauldron", shield: "shield", held: "weapon",
}

/** How a held thing is moved: the game weapon whose clips it plays, and what it asks of the body. */
export interface EmberHandling {
    /** The weapon whose attack clips (`slash:<clips>`) it plays; its own name when absent. */
    clips?: string
    /** Needs both hands: a shield goes on the back. */
    twoHanded?: boolean
    /** Carried upright, butt toward the ground (clips.ts poleCarry). */
    polearm?: boolean
    /** The frame it is drawn on, in pixels (64 + the body's growth when absent: HELD_FRAME). */
    frame?: number
}

/**
 * How an item is laid out for its icon, when the default does not show it: the way a held thing
 * points from the fist (`grip`), the forearm's line (`forearm`), which way its edge faces (`edge`),
 * and a turn about the upright and a tilt toward the viewer, in degrees, of the whole item.
 */
export interface EmberIconPose {
    grip?: [number, number, number]
    forearm?: [number, number, number]
    edge?: [number, number, number]
    turn?: number
    tilt?: number
}

export interface EmberDesign {
    /** The item type it draws. */
    id: string
    slot: EmberSlot
    /** One of the slot's models. */
    model: string
    params?: Record<string, Json>
    /**
     * Parameters computed in the model's scope (expressions), over `params`: what depends on the
     * item's colour or the body, a shield's field `tincture(color)`.
     */
    bind?: Record<string, string>
    /** The game's colour names the item comes in (Sprite Lab's items); the game's own take the item's. */
    colors?: string[]
    handling?: EmberHandling
    /** Its icon's layout (icon.ts), when the default does not show it well. */
    icon?: EmberIconPose
}

export interface EmberModelFile {
    version: 1
    about?: string
    /** Shared named values and functions every model may use, in order. */
    library?: LetList
    models: Partial<Record<EmberModelSlot, Record<string, EmberModel>>>
    designs: EmberDesign[]
}

/** content's bodies.json: the body models (slot `body`) and the library every model sees. */
export interface EmberBodyFile {
    version: 1
    about?: string
    library?: LetList
    models: Record<string, EmberModel>
}

/** content's hair.json: hair models (slot `hair`) and the game's styles, each its model's parameters. */
export interface EmberHairFile {
    version: 1
    about?: string
    library?: LetList
    /** The style a look's unknown style is drawn as. */
    default: string
    /** Styles that are no hair at all (`bald`). */
    none: string[]
    models: Record<string, EmberModel>
    /** Parameters of the style's model (`model`, `shell` by default). */
    styles: Record<string, Record<string, Json>>
}
export const DEFAULT_HAIR_MODEL = "shell"

// ---- compiling -----------------------------------------------------------------------------

type Run = (scope: Scope, out: unknown[]) => void

const isObject = (v: unknown): v is Record<string, any> => !!v && typeof v === "object" && !Array.isArray(v)
const deepFreeze = <T>(v: T): T => {
    if (v && typeof v === "object") {
        Object.values(v as any).forEach(deepFreeze)
        Object.freeze(v)
    }
    return v
}

const withPath = <T>(path: string, f: () => T): T => {
    try {
        return f()
    } catch (e) {
        throw new ExprError(`${path}: ${(e as Error).message}`)
    }
}

export const compileValue = (value: LetValue, path: string): Compiled => {
    if (typeof value === "string") return withPath(path, () => expr(value))
    if (isObject(value) && Array.isArray((value as Record<string, unknown>).fn)) return withPath(path, () => compileFnSource(value as unknown as FnSpec, path))
    const literal = deepFreeze(JSON.parse(JSON.stringify(value)))
    return () => literal
}

const compileLets = (lets: LetList | undefined, path: string): [string, Compiled][] =>
    (lets ?? []).map(([name, value], i) => {
        if (typeof name !== "string" || !/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(name)) throw new ExprError(`${path}.let[${i}]: bad name ${String(name)}`)
        return [name, compileValue(value, `${path}.let[${i}] ${name}`)]
    })

/** Names bound lazily, once per scope: a value nothing reads is never computed (or able to fail). */
const bindLazy = (scope: Scope, lets: [string, Compiled][]) => {
    for (const [name, compiled] of lets) {
        let done = false, value: unknown
        Object.defineProperty(scope, name, {
            enumerable: true,
            configurable: true,
            get: () => {
                if (!done) { value = compiled(scope); done = true }
                return value
            },
        })
    }
}

const collect = (value: unknown, out: unknown[]) => {
    if (value === undefined || value === null || value === false) return
    if (Array.isArray(value)) { for (const v of value) collect(v, out); return }
    out.push(value)
}

const compileParts = (parts: PartSpec[], path: string, models: ModelSet, slot: EmberModelSlot): Run[] =>
    (parts ?? []).map((part, i) => compilePart(part, `${path}[${i}]`, models, slot))

const compilePart = (part: PartSpec, path: string, models: ModelSet, slot: EmberModelSlot): Run => {
    if (typeof part === "string") {
        const c = withPath(path, () => expr(part))
        return (s, out) => collect(c(s), out)
    }
    if (!isObject(part)) throw new ExprError(`${path}: a part is an expression or a part spec`)
    if ("when" in part) {
        const test = withPath(`${path}.when`, () => expr(part.when))
        const yes = compileParts(part.parts, `${path}.parts`, models, slot)
        const no = compileParts(part.else ?? [], `${path}.else`, models, slot)
        return (s, out) => { for (const r of test(s) ? yes : no) r(s, out) }
    }
    if ("each" in part) {
        const list = withPath(`${path}.each`, () => expr(part.each))
        const lets = compileLets(part.let, path)
        const inner = compileParts(part.parts, `${path}.parts`, models, slot)
        const as = part.as
        return (s, out) => {
            const items = list(s)
            if (!Array.isArray(items)) throw new ExprError(`${path}.each: not a list`)
            for (const item of items) {
                const scope = Object.create(s)
                scope[as] = item
                for (const [name, c] of lets) scope[name] = c(scope)
                for (const r of inner) r(scope, out)
            }
        }
    }
    if ("paint" in part) {
        const paint = compileValue(part.paint, `${path}.paint`)
        const inner = compileParts(part.parts, `${path}.parts`, models, slot)
        const over = !!part.over
        return (s, out) => {
            const made: unknown[] = []
            for (const r of inner) r(s, made)
            const f = paint(s)
            if (!f) { out.push(...made); return }
            const builtins = s.$builtins
            out.push(...(over ? builtins.overPainted : builtins.painted)(made, f))
        }
    }
    if ("apply" in part) {
        const fn = withPath(`${path}.apply`, () => expr(part.apply))
        const inner = compileParts(part.parts, `${path}.parts`, models, slot)
        return (s, out) => {
            const made: unknown[] = []
            for (const r of inner) r(s, made)
            const f = fn(s)
            if (!f) { out.push(...made); return }
            if (typeof f !== "function") throw new ExprError(`${path}.apply: not a function`)
            collect(f(made), out)
        }
    }
    if ("use" in part) {
        const [useSlot, name] = part.use.includes("/") ? (part.use.split("/") as [EmberModelSlot, string]) : [slot, part.use]
        const bound = Object.entries(part.bind ?? {}).map(([k, v]) => [k, withPath(`${path}.bind.${k}`, () => expr(v))] as const)
        const literal = part.with ?? {}
        return (s, out) => {
            const params: Record<string, unknown> = { ...s.params, ...literal }
            for (const [k, c] of bound) params[k] = c(s)
            collect(models.run(useSlot, name, params, s.$context), out)
        }
    }
    if ("let" in part) {
        const lets = compileLets(part.let, path)
        const inner = compileParts(part.parts, `${path}.parts`, models, slot)
        return (s, out) => {
            const scope = Object.create(s)
            bindLazy(scope, lets)
            for (const r of inner) r(scope, out)
        }
    }
    throw new ExprError(`${path}: unknown part spec (${Object.keys(part).join(", ")})`)
}

interface CompiledModel {
    params: Record<string, Json>
    lets: [string, Compiled][]
    parts: Run[]
    overBody?: { groups: string[]; paint: Compiled }
}

/** What a model runs with: the posed body and everything the engine knows about drawing it. */
export interface ModelContext {
    [name: string]: unknown
}

/** Paint the engine lays over the body's own surfaces at the end (EmberModel.overBody). */
export interface OverBody {
    groups: string[]
    paint: (world: unknown, normal: unknown) => number | undefined
}

let builtins: Record<string, unknown> = Object.create(null)

/**
 * The names every model can use: the engine's shapes, body volumes, light, materials and maths
 * (body.ts sets them once). A model sees nothing else.
 */
export const setModelBuiltins = (names: Record<string, unknown>): void => {
    builtins = Object.assign(Object.create(null), names)
    builtins.$builtins = builtins
    for (const set of [body, content, preview]) set.reset()
}

/** The names models can use besides their own (setModelBuiltins), for documentation and tooling. */
export const emberModelBuiltins = (): Record<string, unknown> =>
    Object.fromEntries(Object.entries(builtins).filter(([name]) => !name.startsWith("$")))

class ModelSet {
    private compiled = new Map<string, CompiledModel>()
    private library: Scope | undefined
    constructor(public file: EmberModelFile | undefined, private fallback?: ModelSet) {}
    reset() {
        this.compiled.clear()
        this.library = undefined
    }
    private libraryScope(): Scope {
        if (!this.library) {
            const scope = Object.create(this.fallback ? this.fallback.libraryScope() : builtins)
            bindLazy(scope, compileLets(this.file?.library, "library"))
            this.library = scope
        }
        return this.library
    }
    model(slot: EmberModelSlot, name: string): EmberModel | undefined {
        const own = this.file?.models?.[slot]?.[name]
        return own ?? this.fallback?.model(slot, name)
    }
    private compile(slot: EmberModelSlot, name: string): CompiledModel | undefined {
        const k = `${slot}/${name}`
        let c = this.compiled.get(k)
        if (c) return c
        const m = this.model(slot, name)
        if (!m) return undefined
        const path = `models.${slot}.${name}`
        c = {
            params: m.params ?? {},
            lets: compileLets(m.let, path),
            parts: compileParts(m.parts, `${path}.parts`, this, slot),
            overBody: m.overBody ? { groups: m.overBody.groups, paint: compileValue(m.overBody.paint, `${path}.overBody.paint`) } : undefined,
        }
        this.compiled.set(k, c)
        return c
    }
    /** Run a model: the primitives it makes, and paint it lays over the body (pushed onto `overBody`). */
    run(slot: EmberModelSlot, name: string, params: Record<string, unknown>, context: ModelContext, overBody?: OverBody[], bind?: Record<string, string>): unknown[] {
        const c = this.compile(slot, name)
        if (!c) return []
        const scope = Object.create(this.libraryScope())
        Object.assign(scope, context)
        scope.$context = context
        const all = { ...c.params, ...params }
        scope.params = all
        for (const k of Object.keys(all)) scope[k] = all[k]
        if (bind) bindLazy(scope, Object.entries(bind).map(([k, v]) => [k, withPath(`${slot}/${name} bind.${k}`, () => expr(v))]))
        bindLazy(scope, c.lets)
        const out: unknown[] = []
        for (const r of c.parts) r(scope, out)
        if (c.overBody && overBody) overBody.push({ groups: c.overBody.groups, paint: c.overBody.paint(scope) })
        return out
    }
    designs(): EmberDesign[] {
        const own = this.file?.designs ?? []
        const ids = new Set(own.map(d => `${d.slot}:${d.id}`))
        return [...(this.fallback?.designs().filter(d => !ids.has(`${d.slot}:${d.id}`)) ?? []), ...own]
    }
}

/** The body layer (bodies.json, hair.json): under the game's equipment, which sees its library. */
const body = new ModelSet(undefined)
const content = new ModelSet(undefined, body)
let preview = new ModelSet(undefined, content)
let hair: EmberHairFile | undefined
let designIndex = new Map<string, EmberDesign>()
let designKeys = new Map<string, string>()

const fingerprintOf = (value: unknown): string => {
    const text = JSON.stringify(value)
    let h = 2166136261
    for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 16777619)
    return (h >>> 0).toString(36)
}

const listeners: (() => void)[] = []
/** Call `listener` now and whenever the models or designs change (content activated, a preview set). */
export const onEmberModelsChanged = (listener: () => void): void => {
    listeners.push(listener)
    listener()
}
let revision = ""
/**
 * Changes whenever anything Ember draws from content does -- a model, a design, a library, the body
 * layer, or a table (palettes, styles, marks, humanoids: noteEmberContent): part of every baked
 * Ember frame's cache key.
 */
export const emberModelsRevision = (): string => revision
const parts = new Map<string, string>()

const reindex = () => {
    designIndex = new Map(preview.designs().map(d => [`${d.slot}:${d.id}`, d]))
    designKeys = new Map()
    parts.set("preview", fingerprintOf(preview.file ?? null))
    revision = fingerprintOf([...parts.entries()].sort())
    for (const listener of listeners) listener()
}

/** A table the renderer draws with changed (the module that owns it calls this when it is bound). */
export const noteEmberContent = (part: string, value: unknown): void => {
    parts.set(part, fingerprintOf(value ?? null))
    reindex()
}

/**
 * The body layer from content: the body models (bodies.json, slot `body`) and hair (hair.json,
 * slot `hair`, and its styles), their libraries under every equipment model's. Replaces the last.
 */
export const registerEmberBodies = (bodies: EmberBodyFile | undefined, hairFile: EmberHairFile | undefined): void => {
    body.file = bodies || hairFile ? {
        version: 1,
        library: [...(bodies?.library ?? []), ...(hairFile?.library ?? [])],
        models: { body: bodies?.models ?? {}, hair: hairFile?.models ?? {} },
        designs: [],
    } : undefined
    hair = hairFile
    for (const set of [body, content, preview]) set.reset()
    parts.set("bodies", fingerprintOf([bodies ?? null, hairFile ?? null]))
    reindex()
}

/** A look's hair style as its model and parameters; none for a style that is no hair, `default` for one the game lacks. */
export const emberHairStyle = (style: string): { model: string; params: Record<string, Json> } | undefined => {
    if (!hair || hair.none.includes(style)) return undefined
    const own = hair.styles[style] ?? hair.styles[hair.default]
    if (!own) return undefined
    const { model, about, ...params } = own as Record<string, Json>
    return { model: typeof model === "string" ? model : DEFAULT_HAIR_MODEL, params }
}
/** The game's hair styles, by name: each its model's parameters. */
export const emberHairStyles = (): Record<string, Record<string, Json>> => hair?.styles ?? {}

/** The game's models and designs (bound from the content catalog); replaces the previous set. */
export const registerEmberModels = (file: EmberModelFile | undefined): void => {
    content.file = file
    content.reset()
    preview.reset()
    parts.set("equipment", fingerprintOf(file ?? null))
    reindex()
}

/**
 * Models and designs a tool is previewing, laid over the game's: a draft is drawn under its item
 * type before it exists, and a saved one can be iterated on. Undefined clears them.
 */
export const setPreviewEmberModels = (file: Partial<EmberModelFile> | undefined): void => {
    preview = new ModelSet(file ? { version: 1, models: {}, designs: [], ...file } : undefined, content)
    reindex()
}

/** The design for item type `id` in `slot`, if Ember draws it. */
export const emberDesign = (slot: EmberSlot, id: string | undefined): EmberDesign | undefined =>
    id === undefined ? undefined : designIndex.get(`${slot}:${id}`)

/** Every design, optionally of one slot. */
export const emberDesigns = (slot?: EmberSlot): EmberDesign[] => [...designIndex.values()].filter(d => !slot || d.slot === slot)

/** A model of a slot, as written. */
export const emberModel = (slot: EmberModelSlot, name: string): EmberModel | undefined => preview.model(slot, name)

/** Every model name of a slot. */
export const emberModelNames = (slot: EmberModelSlot): string[] =>
    [...new Set([body, content, preview].flatMap(set => Object.keys(set.file?.models?.[slot] ?? {})))]

/**
 * Part of a frame cache key for an item drawn by a design: changes whenever the design or its
 * model does, so a preview of an edited model is never served frames baked from the last one.
 */
export const emberDesignKey = (slot: EmberSlot, id: string | undefined): string => {
    if (id === undefined) return ""
    const k = `${slot}:${id}`
    let key = designKeys.get(k)
    if (key === undefined) {
        const d = designIndex.get(k)
        key = d ? fingerprintOf([d, preview.model(d.slot, d.model), preview.file?.library ?? null]) : ""
        designKeys.set(k, key)
    }
    return key
}

/** Run a slot's model by name (engine use: body.ts). */
export const runEmberModel = (slot: EmberModelSlot, name: string, params: Record<string, unknown>, context: ModelContext, overBody?: OverBody[], bind?: Record<string, string>): unknown[] =>
    preview.run(slot, name, params, context, overBody, bind)

/** Every expression in a model file compiles (a parse error names its path). Returns the problems. */
export const emberModelFileProblems = (file: unknown, slots: readonly EmberModelSlot[] = EMBER_SLOTS): string[] => {
    const problems: string[] = []
    if (!isObject(file)) return ["expected an object"]
    if (file.version !== 1) problems.push("version: expected 1")
    const set = new ModelSet(file as EmberModelFile)
    try { compileLets(file.library as LetList, "library") } catch (e) { problems.push((e as Error).message) }
    if (!isObject(file.models)) problems.push("models: expected slot -> name -> model")
    else for (const [slot, models] of Object.entries(file.models)) {
        if (!slots.includes(slot as EmberModelSlot)) { problems.push(`models.${slot}: not a slot (${slots.join(", ")})`); continue }
        if (!isObject(models)) { problems.push(`models.${slot}: expected name -> model`); continue }
        for (const [name, model] of Object.entries(models)) {
            if (!isObject(model) || !Array.isArray(model.parts)) { problems.push(`models.${slot}.${name}: a model has parts`); continue }
            try {
                compileLets(model.let as LetList, `models.${slot}.${name}`)
                compileParts(model.parts as PartSpec[], `models.${slot}.${name}.parts`, set, slot as EmberModelSlot)
                if (model.overBody) compileValue(model.overBody.paint, `models.${slot}.${name}.overBody.paint`)
            } catch (e) { problems.push((e as Error).message) }
        }
    }
    if (!Array.isArray(file.designs)) problems.push("designs: expected a list")
    else {
        const seen = new Set<string>()
        file.designs.forEach((d: any, i: number) => {
            const at = `designs[${i}]`
            if (!isObject(d)) { problems.push(`${at}: expected an object`); return }
            if (typeof d.id !== "string" || !/^[a-z][a-z0-9_-]{1,47}$/.test(d.id)) problems.push(`${at}.id: 2–48 lowercase letters, digits, - or _`)
            if (!EMBER_SLOTS.includes(d.slot)) problems.push(`${at}.slot: one of ${EMBER_SLOTS.join(", ")}`)
            else if (!set.model(d.slot, d.model)) problems.push(`${at}.model: ${d.slot} has no model ${d.model}`)
            if (seen.has(`${d.slot}:${d.id}`)) problems.push(`${at}: ${d.slot} ${d.id} is designed twice`)
            seen.add(`${d.slot}:${d.id}`)
            if (d.params !== undefined && !isObject(d.params)) problems.push(`${at}.params: expected an object`)
            // A design sets only what its model declares: a misspelt or dropped parameter would otherwise do nothing, silently.
            const model = EMBER_SLOTS.includes(d.slot) ? set.model(d.slot, d.model) : undefined
            if (model) {
                const declared = Object.keys(model.params ?? {})
                for (const k of [...Object.keys(isObject(d.params) ? d.params : {}), ...Object.keys(isObject(d.bind) ? d.bind : {})])
                    if (!declared.includes(k)) problems.push(`${at}: ${d.slot}/${d.model} has no parameter ${k} (it has ${declared.join(", ") || "none"})`)
            }
            if (d.bind !== undefined) {
                if (!isObject(d.bind)) problems.push(`${at}.bind: name -> expression`)
                else for (const [k, v] of Object.entries(d.bind)) {
                    if (typeof v !== "string") { problems.push(`${at}.bind.${k}: an expression`); continue }
                    try { expr(v) } catch (e) { problems.push(`${at}.bind.${k}: ${(e as Error).message}`) }
                }
            }
            if (d.colors !== undefined && (!Array.isArray(d.colors) || d.colors.some((c: unknown) => typeof c !== "string"))) problems.push(`${at}.colors: a list of colour names`)
            if (d.icon !== undefined) {
                const vec = (v: unknown) => Array.isArray(v) && v.length === 3 && v.every(n => typeof n === "number" && Number.isFinite(n))
                if (!isObject(d.icon)) problems.push(`${at}.icon: expected an object`)
                else for (const [k, v] of Object.entries(d.icon)) {
                    if (["grip", "forearm", "edge"].includes(k)) { if (!vec(v)) problems.push(`${at}.icon.${k}: [x, y, z]`) }
                    else if (["turn", "tilt"].includes(k)) { if (typeof v !== "number" || !Number.isFinite(v)) problems.push(`${at}.icon.${k}: degrees`) }
                    else problems.push(`${at}.icon.${k}: unknown (grip, forearm, edge, turn, tilt)`)
                }
            }
            if (d.handling !== undefined) {
                if (d.slot !== "held") problems.push(`${at}.handling: only a held design has handling`)
                else if (!isObject(d.handling)) problems.push(`${at}.handling: expected an object`)
                else {
                    const h = d.handling
                    if (h.frame !== undefined && (!Number.isInteger(h.frame) || h.frame < 64 || h.frame > 192)) problems.push(`${at}.handling.frame: 64–192 pixels`)
                    for (const k of ["twoHanded", "polearm"]) if (h[k] !== undefined && typeof h[k] !== "boolean") problems.push(`${at}.handling.${k}: true or false`)
                    if (h.clips !== undefined && typeof h.clips !== "string") problems.push(`${at}.handling.clips: a weapon name`)
                }
            }
        })
    }
    return problems
}

/** The model the engine draws a body with when its look names none (EmberLook.body). */
export const DEFAULT_BODY_MODEL = "human"

/**
 * The body layer's files (bodies.json, hair.json) load: every expression compiles, the default body
 * and hair models exist, and every hair style sets only parameters its model declares. Returns the
 * problems, each with its file.
 */
export const emberBodyProblems = (bodies: unknown, hairFile: unknown): { file: "bodies" | "hair"; message: string }[] => {
    const problems: { file: "bodies" | "hair"; message: string }[] = []
    const of = (file: "bodies" | "hair", messages: string[]) => problems.push(...messages.map(message => ({ file, message })))
    const shape = (file: "bodies" | "hair", value: unknown): value is Record<string, any> => {
        if (!isObject(value)) { of(file, ["expected an object"]); return false }
        if (value.version !== 1) of(file, ["version: expected 1"])
        if (!isObject(value.models)) { of(file, ["models: expected name -> model"]); return false }
        return true
    }
    for (const [file, value, slot] of [["bodies", bodies, "body"], ["hair", hairFile, "hair"]] as const) {
        if (!shape(file, value)) continue
        of(file, emberModelFileProblems({ version: 1, library: value.library, models: { [slot]: value.models }, designs: [] }, EMBER_BODY_SLOTS)
            .map(m => m.replace(`models.${slot}.`, "models.")))
    }
    if (isObject(bodies) && isObject(bodies.models) && !isObject(bodies.models[DEFAULT_BODY_MODEL])) of("bodies", [`models.${DEFAULT_BODY_MODEL}: the body a look that names none is drawn with`])
    if (!isObject(hairFile) || !isObject(hairFile.models)) return problems
    if (!isObject(hairFile.styles)) { of("hair", ["styles: expected name -> parameters"]); return problems }
    if (typeof hairFile.default !== "string" || !isObject(hairFile.styles[hairFile.default])) of("hair", ["default: one of the styles"])
    if (!Array.isArray(hairFile.none) || hairFile.none.some((n: unknown) => typeof n !== "string")) of("hair", ["none: a list of style names"])
    for (const [name, style] of Object.entries(hairFile.styles)) {
        if (!isObject(style)) { of("hair", [`styles.${name}: expected parameters`]); continue }
        const modelName = style.model ?? DEFAULT_HAIR_MODEL
        const model = typeof modelName === "string" ? hairFile.models[modelName] : undefined
        if (!isObject(model)) { of("hair", [`styles.${name}.model: hair has no model ${String(modelName)}`]); continue }
        const declared = Object.keys(isObject(model.params) ? model.params : {})
        for (const k of Object.keys(style)) if (k !== "model" && k !== "about" && !declared.includes(k)) of("hair", [`styles.${name}: ${modelName} has no parameter ${k} (it has ${declared.join(", ") || "none"})`])
    }
    return problems
}
